import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  GetApp,
  Visibility,
  Description,
  Instagram,
  YouTube,
  Edit,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    borderLeft: `4px solid #f9a620`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: theme.shadows[8],
    },
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  iconContainer: {
    height: 40,
    width: 40,
    borderRadius: theme.shape.borderRadius,
    background: '#f9a620',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    color: '#fff',
    fontSize: 20,
  },
  headerContent: {
    flex: 1,
    minWidth: 0,
  },
  brandName: {
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
  },
  brandDescription: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  badges: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  competitors: {
    marginBottom: theme.spacing(2),
  },
  competitorsLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  competitorChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  actionButton: {
    flex: 1,
  },
  viewButton: {
    backgroundColor: '#f9a620',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#e6950e',
    },
  },
}));

const getTypeBadgeColor = (type) => {
  switch (type) {
    case 'Pre-generated':
      return { bg: '#dbeafe', text: '#1e40af' };
    case 'Template':
      return { bg: '#d1fae5', text: '#065f46' };
    case 'Historical':
      return { bg: '#e9d5ff', text: '#6b21a8' };
    default:
      return { bg: '#f3f4f6', text: '#374151' };
  }
};

const getPlatformIcon = (platform) => {
  if (platform === 'Instagram') return Instagram;
  if (platform === 'YouTube') return YouTube;
  return Description;
};

export function BrandCard({ brand, onViewReport, onExportReport, onEditReport }) {
  const classes = useStyles();
  const reports = React.useMemo(() => brand.reports || [], [brand.reports]);

  // Collect all unique competitors from all reports
  const allCompetitors = React.useMemo(() => {
    const competitorSet = new Set();
    reports.forEach(report => {
      if (report.competitors_analyzed && Array.isArray(report.competitors_analyzed)) {
        report.competitors_analyzed.forEach(comp => {
          const handle = comp.handle || comp.name || comp;
          if (handle && handle !== 'Test - blank YT') {
            competitorSet.add(handle);
          }
        });
      }
    });
    return Array.from(competitorSet);
  }, [reports]);

  // Get the most common report type and platform
  const reportType = React.useMemo(() => {
    if (reports.length === 0) return 'Pre-generated';
    const types = reports.map(r => 
      r.tags && r.tags.length > 0 ? r.tags[0].type : r.type || 'Pre-generated'
    );
    return types[0]; // Use first report's type
  }, [reports]);

  const platform = React.useMemo(() => {
    return reports[0]?.platform || 'Instagram';
  }, [reports]);

  // Get generated date (use most recent or first)
  const generatedDate = React.useMemo(() => {
    return reports[0]?.generated_on || '';
  }, [reports]);

  const typeColors = getTypeBadgeColor(reportType);
  const PlatformIcon = getPlatformIcon(platform);

  const handleViewReport = (e) => {
    e.stopPropagation();
    // Pass brand_id for brand-level view
    if (onViewReport && brand.brand_id) {
      onViewReport({
        brand_id: brand.brand_id,
        report_group_id: brand.report_group_id,
        brand_name: brand.brand_name,
        platform: platform,
        report_title: reports[0].report_title || brand.brand_name,
        ids: reports.map(r => r.id).join(','),
        start_date: reports[0]?.start_date || reports[0]?.start_date_range || null,
        end_date: reports[0]?.end_date || reports[0]?.end_date_range || null,
      });
    }
  };

  const handleExportReport = (e) => {
    e.stopPropagation();
    // Export the first report or handle brand-level export
    if (reports.length > 0 && onExportReport) {
      onExportReport(reports[0]);
    }
  };

  const handleEditReport = (e) => {
    e.stopPropagation();
    if (onEditReport && brand.brand_id && reports.length > 0) {
      onEditReport({
        brand_id: brand.brand_id,
        report_group_id: brand.report_group_id,
        ids: reports.map((r) => r.id),
        platform: platform,
        report_title: reports[0].report_title || brand.brand_name || brand.grouping_key,
      });
    }
  };

  // Create a combined description from all reports
  const combinedDescription = React.useMemo(() => {
    if (reports.length === 0) return '';
    if (reports.length === 1) return reports[0].description || '';
    return `Complete performance analysis with ${reports.length} reports across ${platform} metrics.`;
  }, [reports, platform]);

  return (
    <Card className={classes.card} onClick={handleViewReport}>
      <CardContent>
        <Box className={classes.header}>
          <Box className={classes.iconContainer}>
            <Description className={classes.icon} />
          </Box>
          <Box className={classes.headerContent}>
            <Typography className={classes.brandName} noWrap>
              {brand.grouping_key}
            </Typography>
            <Typography className={classes.brandDescription}>
              {combinedDescription}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.badges}>
          {reports.some(r => r.status === 'processing') && (
            <Chip
              label="Processing"
              size="small"
              style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                fontWeight: 600,
              }}
            />
          )}
          <Chip
            label={reportType}
            size="small"
            style={{
              backgroundColor: typeColors.bg,
              color: typeColors.text,
            }}
          />
          {platform && (
            <Chip
              icon={<PlatformIcon style={{ fontSize: 12 }} />}
              label={platform}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {allCompetitors.length > 0 && (
          <Box className={classes.competitors}>
            <Typography className={classes.competitorsLabel}>
              Competitors Analyzed
            </Typography>
            <Box className={classes.competitorChips}>
              {allCompetitors.map((competitor, index) => (
                <Chip
                  key={index}
                  label={competitor}
                  size="small"
                  variant="outlined"
                  style={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {generatedDate && (
          <Typography className={classes.date}>
            Generated on {generatedDate}
          </Typography>
        )}

        <Box className={classes.actions}>
          <Button
            variant="contained"
            startIcon={<Visibility />}
            className={`${classes.actionButton} ${classes.viewButton}`}
            onClick={(e) => {
              e.stopPropagation();
              handleViewReport(e);
            }}
            size="small"
          >
            View Details
          </Button>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditReport(e);
            }}
            size="small"
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={(e) => {
              e.stopPropagation();
              handleExportReport(e);
            }}
            size="small"
          >
            Export
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

